import { create } from 'zustand';
import { GameSettings } from '@solante/shared';
import { soundManager, SoundCategorySettings } from '../utils/sounds';

type ExtendedSettings = GameSettings & {
  soundEnabled: boolean;
  soundCategories: SoundCategorySettings;
  // Mobile settings
  oneHandedMode: boolean;
  reducedAnimations: boolean;
  largeTouchTargets: boolean;
};

type SettingsState = ExtendedSettings & {
  toggleMotion: () => void;
  toggleColorBlind: () => void;
  toggleAutoplay: () => void;
  toggleSound: () => void;
  toggleSoundCategory: (category: keyof SoundCategorySettings) => void;
  // Mobile toggles
  toggleOneHandedMode: () => void;
  toggleReducedAnimations: () => void;
  toggleLargeTouchTargets: () => void;
};

const STORAGE_KEY = 'solante:settings';

const DEFAULT_SOUND_CATEGORIES: SoundCategorySettings = {
  ui: true,
  gameplay: true,
  notification: true,
};

const loadSettings = (): ExtendedSettings => {
  const defaults: ExtendedSettings = {
    motionReduced: false,
    colorBlindMode: false,
    autoplayAnimations: true,
    soundEnabled: true,
    soundCategories: { ...DEFAULT_SOUND_CATEGORIES },
    oneHandedMode: false,
    reducedAnimations: false,
    largeTouchTargets: false,
  };

  if (typeof window === 'undefined') {
    return defaults;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return defaults;
  }

  try {
    const parsed = JSON.parse(stored) as Partial<ExtendedSettings>;
    return {
      motionReduced: parsed.motionReduced ?? false,
      colorBlindMode: parsed.colorBlindMode ?? false,
      autoplayAnimations: parsed.autoplayAnimations ?? true,
      soundEnabled: parsed.soundEnabled ?? true,
      soundCategories: {
        ui: parsed.soundCategories?.ui ?? true,
        gameplay: parsed.soundCategories?.gameplay ?? true,
        notification: parsed.soundCategories?.notification ?? true,
      },
      oneHandedMode: parsed.oneHandedMode ?? false,
      reducedAnimations: parsed.reducedAnimations ?? false,
      largeTouchTargets: parsed.largeTouchTargets ?? false,
    };
  } catch (error) {
    console.warn('Failed to parse settings', error);
    return defaults;
  }
};

export const useSettingsStore = create<SettingsState>((set, get) => ({
  ...loadSettings(),
  toggleMotion: () => {
    const next = { ...get(), motionReduced: !get().motionReduced };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleColorBlind: () => {
    const next = { ...get(), colorBlindMode: !get().colorBlindMode };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleAutoplay: () => {
    const next = { ...get(), autoplayAnimations: !get().autoplayAnimations };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleSound: () => {
    const newEnabled = !get().soundEnabled;
    soundManager.setEnabled(newEnabled);
    const next = { ...get(), soundEnabled: newEnabled };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleSoundCategory: (category: keyof SoundCategorySettings) => {
    const currentCategories = get().soundCategories;
    const newEnabled = !currentCategories[category];
    soundManager.setCategoryEnabled(category, newEnabled);
    const next = {
      ...get(),
      soundCategories: {
        ...currentCategories,
        [category]: newEnabled,
      },
    };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleOneHandedMode: () => {
    const next = { ...get(), oneHandedMode: !get().oneHandedMode };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleReducedAnimations: () => {
    const next = { ...get(), reducedAnimations: !get().reducedAnimations };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
  toggleLargeTouchTargets: () => {
    const next = { ...get(), largeTouchTargets: !get().largeTouchTargets };
    set(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  },
}));

// Initialize sound manager with stored settings
if (typeof window !== 'undefined') {
  const settings = loadSettings();
  soundManager.setEnabled(settings.soundEnabled);
  soundManager.setCategorySettings(settings.soundCategories);
}
